import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '@/stores/uiStore';
import { usePermissions } from '@/stores/authStore';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Calculator,
  Users,
  Package,
  ShoppingCart,
  ShoppingBag,
  UserCog,
  Factory,
  Store,
  FolderKanban,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';

// ============================================
// NAVIGATION ITEMS
// ============================================

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href?: string;
  children?: {
    label: string;
    href: string;
    permission?: string;
  }[];
  permission?: string;
}

const navigationItems: NavItem[] = [
  {
    id: 'dashboard',
    label: 'common.dashboard',
    icon: <LayoutDashboard className="h-5 w-5" />,
    href: '/dashboard',
  },
  {
    id: 'accounting',
    label: 'modules.accounting',
    icon: <Calculator className="h-5 w-5" />,
    permission: 'accounting:view',
    children: [
      { label: 'accounting.chartOfAccounts', href: '/accounting/accounts' },
      { label: 'accounting.journalEntries', href: '/accounting/journal-entries' },
      { label: 'accounting.taxes', href: '/accounting/taxes' },
      { label: 'accounting.fiscalYear', href: '/accounting/fiscal-years' },
      { label: 'common.payments', href: '/accounting/payments' },
      { label: 'accounting.reports', href: '/accounting/reports', permission: 'accounting:reports:view' },
    ],
  },
  {
    id: 'partners',
    label: 'modules.partners',
    icon: <Users className="h-5 w-5" />,
    href: '/partners',
    permission: 'customers:view',
  },
  {
    id: 'inventory',
    label: 'modules.inventory',
    icon: <Package className="h-5 w-5" />,
    permission: 'inventory:view',
    children: [
      { label: 'inventory.products', href: '/inventory/products' },
      { label: 'inventory.categories', href: '/inventory/categories' },
      { label: 'inventory.units', href: '/inventory/units' },
      { label: 'inventory.warehouses', href: '/inventory/warehouses' },
      { label: 'inventory.stockMoves', href: '/inventory/stock-moves' },
    ],
  },
  {
    id: 'sales',
    label: 'modules.sales',
    icon: <ShoppingCart className="h-5 w-5" />,
    permission: 'sales:view',
    children: [
      { label: 'sales.salesOrders', href: '/sales/orders' },
      { label: 'sales.invoices', href: '/sales/invoices' },
      { label: 'sales.deliveries', href: '/sales/deliveries' },
    ],
  },
  {
    id: 'purchase',
    label: 'modules.purchase',
    icon: <ShoppingBag className="h-5 w-5" />,
    permission: 'purchase:view',
    children: [
      { label: 'purchase.purchaseOrders', href: '/purchase/orders' },
      { label: 'purchase.supplierInvoice', href: '/purchase/invoices' },
      { label: 'purchase.reception', href: '/purchase/receptions' },
    ],
  },
  {
    id: 'hr',
    label: 'modules.hr',
    icon: <UserCog className="h-5 w-5" />,
    permission: 'hr:view',
    children: [
      { label: 'hr.employees', href: '/hr/employees' },
      { label: 'hr.contracts', href: '/hr/contracts' },
      { label: 'hr.positions', href: '/hr/positions' },
      { label: 'hr.departments', href: '/hr/departments' },
      { label: 'hr.payslips', href: '/hr/payslips', permission: 'payroll:view' },
      { label: 'hr.leaves', href: '/hr/leaves' },
      { label: 'hr.attendance', href: '/hr/attendance' },
    ],
  },
  {
    id: 'manufacturing',
    label: 'modules.manufacturing',
    icon: <Factory className="h-5 w-5" />,
    permission: 'manufacturing:view',
    children: [
      { label: 'manufacturing.boms', href: '/manufacturing/boms' },
      { label: 'manufacturing.workCenters', href: '/manufacturing/work-centers' },
      { label: 'manufacturing.manufacturingOrders', href: '/manufacturing/orders' },
    ],
  },
  {
    id: 'pos',
    label: 'modules.pos',
    icon: <Store className="h-5 w-5" />,
    permission: 'pos:view',
    children: [
      { label: 'pos.pointOfSale', href: '/pos' },
      { label: 'pos.sessions', href: '/pos/sessions' },
      { label: 'pos.orders', href: '/pos/orders' },
      { label: 'common.settings', href: '/pos/configs' },
    ],
  },
  {
    id: 'projects',
    label: 'modules.projects',
    icon: <FolderKanban className="h-5 w-5" />,
    permission: 'projects:view',
    children: [
      { label: 'projects.projects', href: '/projects' },
      { label: 'projects.tasks', href: '/projects/tasks' },
      { label: 'projects.timesheets', href: '/projects/timesheets' },
    ],
  },
  {
    id: 'documents',
    label: 'modules.documents',
    icon: <FileText className="h-5 w-5" />,
    href: '/documents',
    permission: 'documents:view',
  },
  {
    id: 'settings',
    label: 'common.settings',
    icon: <Settings className="h-5 w-5" />,
    permission: 'settings:view',
    children: [
      { label: 'settings.general', href: '/settings' },
      { label: 'settings.users', href: '/settings/users', permission: 'users:view' },
      { label: 'settings.company', href: '/settings/company', permission: 'settings:company:view' },
    ],
  },
];

// ============================================
// SIDEBAR COMPONENT
// ============================================

export default function Sidebar() {
  useTranslation(); // For i18n support
  const location = useLocation();
  const { sidebarOpen, sidebarCollapsed, setSidebarOpen, toggleSidebarCollapsed } = useUIStore();
  const { hasPermission } = usePermissions();
  
  // Filter navigation items based on permissions
  const filteredNavItems = navigationItems.filter((item) => {
    if (item.permission && !hasPermission(item.permission)) return false;
    return true;
  });
  
  // Check if current path matches nav item
  const isActive = (href?: string) => {
    if (!href) return false;
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };
  
  // Check if any child is active
  const hasActiveChild = (children?: NavItem['children']) => {
    if (!children) return false;
    return children.some((child) => isActive(child.href));
  };
  
  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-40 flex flex-col bg-card border-r transition-all duration-300',
        sidebarOpen ? (sidebarCollapsed ? 'w-16' : 'w-64') : '-translate-x-full',
        'lg:translate-x-0'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between h-16 px-4 border-b">
        {/* {!sidebarCollapsed && (
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">ERP</span>
            </div>
            <span className="font-semibold text-foreground">Tunisia ERP</span>
          </Link>
        )} */}
        
        {/* Mobile close button */}
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden p-2 rounded-lg hover:bg-accent"
        >
          <X className="h-5 w-5" />
        </button>
        
        {/* Collapse button (desktop) */}
        <button
          onClick={toggleSidebarCollapsed}
          className="hidden lg:flex p-2 rounded-lg hover:bg-accent"
        >
          {sidebarCollapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <ChevronLeft className="h-5 w-5" />
          )}
        </button>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2">
        <ul className="space-y-1">
          {filteredNavItems.map((item) => (
            <NavItemComponent
              key={item.id}
              item={item}
              isActive={isActive}
              hasActiveChild={hasActiveChild}
              collapsed={sidebarCollapsed}
              hasPermission={hasPermission}
            />
          ))}
        </ul>
      </nav>
      
      {/* Footer */}
      {!sidebarCollapsed && (
        <div className="p-4 border-t">
          <p className="text-xs text-muted-foreground text-center">
            v1.0.0
          </p>
        </div>
      )}
    </aside>
  );
}

// ============================================
// NAV ITEM COMPONENT
// ============================================

interface NavItemProps {
  item: NavItem;
  isActive: (href?: string) => boolean;
  hasActiveChild: (children?: NavItem['children']) => boolean;
  collapsed: boolean;
  hasPermission: (permission: string) => boolean;
}

function NavItemComponent({
  item,
  isActive,
  hasActiveChild,
  collapsed,
  hasPermission,
}: NavItemProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(hasActiveChild(item.children));
  
  // Filter children based on permissions
  const filteredChildren = item.children?.filter((child) => {
    if (child.permission && !hasPermission(child.permission)) return false;
    return true;
  });
  
  // Single item (no children)
  if (item.href && !item.children) {
    return (
      <li>
        <Link
          to={item.href}
          className={cn(
            'sidebar-item',
            isActive(item.href) && 'active',
            collapsed && 'justify-center px-2'
          )}
          title={collapsed ? t(item.label) : undefined}
        >
          {item.icon}
          {!collapsed && <span>{t(item.label)}</span>}
        </Link>
      </li>
    );
  }
  
  // Item with children
  return (
    <li>
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'sidebar-item w-full',
          hasActiveChild(filteredChildren) && 'text-accent-foreground',
          collapsed && 'justify-center px-2'
        )}
        title={collapsed ? t(item.label) : undefined}
      >
        {item.icon}
        {!collapsed && (
          <>
            <span className="flex-1 text-left">{t(item.label)}</span>
            <ChevronRight
              className={cn(
                'h-4 w-4 transition-transform',
                expanded && 'rotate-90'
              )}
            />
          </>
        )}
      </button>
      
      {!collapsed && expanded && filteredChildren && (
        <ul className="ml-6 mt-1 space-y-1 border-l pl-2">
          {filteredChildren.map((child) => (
            <li key={child.href}>
              <Link
                to={child.href}
                className={cn(
                  'sidebar-item text-sm',
                  isActive(child.href) && 'active'
                )}
              >
                {t(child.label)}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

// Import React for useState
import React from 'react';
